import { HStack, IconButton, Stack, Text } from "@chakra-ui/react";
import { format } from "date-fns";
import * as React from "react";
import { LuHistory, LuPlus, LuTarget, LuTrash } from "react-icons/lu";
import { Navigate, useParams } from "react-router-dom";

import {
  ConversionAction,
  ConversionActionPlatform,
  ConversionActionTemplate,
  deleteConversionAction,
  getConversionActionTemplates,
  getConversionActions,
} from "../api/conversion-action";
import { useBreadcrumbs } from "../components/breadcrumbs";
import { Button } from "../components/button";
import { ConversionActionSyncedEventsDialog } from "../components/conversion-action-synced-events-dialog";
import { ConversionActionTemplatesDialog } from "../components/conversion-action-templates-dialog";
import { CreateConversionActionDialog } from "../components/create-conversion-action-dialog";
import DataTable, { DataTableColumn } from "../components/data-table";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "../components/dialog";
import { EmptyState } from "../components/empty-state";
import IconCircle from "../components/icon-circle";
import Page from "../components/page";
import PageBody from "../components/page-body";
import PageHeader from "../components/page-header";
import {
  useAppDispatch,
  useAppSelector,
  useLoading,
  useToaster,
  useWorkspaceURL,
} from "../hooks";
import { brand } from "../lib/brand";
import { loadIntegrations } from "../store/workspaceSlice";
import { IntegrationStatus } from "../types/integration";

const PLATFORM_MAP: Record<string, ConversionActionPlatform> = {
  google: ConversionActionPlatform.GOOGLE_ADS,
  meta: ConversionActionPlatform.META,
};

const PLATFORM_LABELS: Record<ConversionActionPlatform, string> = {
  [ConversionActionPlatform.GOOGLE_ADS]: "Google",
  [ConversionActionPlatform.META]: "Meta",
};

const PLATFORM_ADS_LABELS: Record<ConversionActionPlatform, string> = {
  [ConversionActionPlatform.GOOGLE_ADS]: "Google Ads",
  [ConversionActionPlatform.META]: "Meta",
};

const AGENT_NAME = brand.productName;

export default function ConversionActionsPage() {
  const { platform: platformParam } = useParams<{ platform: string }>();
  const platform = platformParam ? PLATFORM_MAP[platformParam] : undefined;

  const dispatch = useAppDispatch();
  const workspaceSlug = useAppSelector((s) => s.workspace.slug);
  const integrations = useAppSelector((s) => s.workspace.integrations);
  const workspaceURL = useWorkspaceURL();
  const [conversionActions, setConversionActions] = React.useState<
    ConversionAction[]
  >([]);
  const [templates, setTemplates] = React.useState<ConversionActionTemplate[]>(
    [],
  );
  const [isLoading, withLoading] = useLoading();
  const [hasLoadedOnce, setHasLoadedOnce] = React.useState(false);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [isTemplatesDialogOpen, setIsTemplatesDialogOpen] =
    React.useState(false);
  const [syncedEventsAction, setSyncedEventsAction] =
    React.useState<ConversionAction | null>(null);
  const [deleteTarget, setDeleteTarget] =
    React.useState<ConversionAction | null>(null);
  const [isDeleting, withDeleting] = useLoading();
  const withToaster = useToaster();

  const label = platform ? PLATFORM_LABELS[platform] : "";
  useBreadcrumbs([
    { label: "Dashboard", to: workspaceURL("") },
    // Google reaches this page from its campaigns hub, so the jump back
    // is one click rather than the browser's back button.
    {
      label,
      to:
        platform === ConversionActionPlatform.GOOGLE_ADS
          ? workspaceURL("google-ads-accounts")
          : undefined,
    },
    { label: "Conversion Agent" },
  ]);

  React.useEffect(() => {
    dispatch(loadIntegrations());
  }, [dispatch]);

  const loadConversionActions = React.useCallback(async () => {
    if (!workspaceSlug || !platform) return;
    await withLoading(async () => {
      await withToaster(async () => {
        const data = await getConversionActions(workspaceSlug, platform);
        setConversionActions(data);
        setHasLoadedOnce(true);
      });
    });
  }, [platform, withLoading, withToaster, workspaceSlug]);

  React.useEffect(() => {
    loadConversionActions();
  }, [loadConversionActions]);

  const isMetaConnected = integrations.some(
    (i) => i.id === "facebook" && i.status === IntegrationStatus.connected,
  );

  React.useEffect(() => {
    if (!workspaceSlug || platform !== ConversionActionPlatform.META) return;
    if (!isMetaConnected) return;
    getConversionActionTemplates(workspaceSlug)
      .then(setTemplates)
      .catch(() => {});
  }, [isMetaConnected, platform, workspaceSlug]);

  const handleDelete = React.useCallback(async () => {
    if (!workspaceSlug || !deleteTarget) return;
    await withDeleting(async () => {
      await withToaster(async () => {
        await deleteConversionAction(workspaceSlug, deleteTarget.id);
        setDeleteTarget(null);
        await loadConversionActions();
      });
    });
  }, [
    deleteTarget,
    loadConversionActions,
    withDeleting,
    withToaster,
    workspaceSlug,
  ]);

  if (!platform) {
    return <Navigate to="." replace />;
  }

  const adsLabel = PLATFORM_ADS_LABELS[platform];

  const columns: DataTableColumn<ConversionAction>[] = [
    {
      name: "Name",
      cell: ({ value }) => (
        <Text fontWeight="medium" truncate title={value.name}>
          {value.name}
        </Text>
      ),
    },
    {
      name: "Created",
      cell: ({ value }) => (
        <Text color="fg.muted" whiteSpace="nowrap">
          {format(new Date(value["created-at"]), "MMM d, yyyy")}
        </Text>
      ),
    },
    {
      name: "",
      align: "end",
      cell: ({ value }) => (
        <HStack gap="1" justify="flex-end" onClick={(e) => e.stopPropagation()}>
          <IconButton
            aria-label="View conversion log"
            size="sm"
            title="View conversion log"
            variant="ghost"
            onClick={() => setSyncedEventsAction(value)}
          >
            <LuHistory size="16" />
          </IconButton>
          <IconButton
            aria-label="Delete conversion"
            color="red.500"
            size="sm"
            title="Delete conversion"
            variant="ghost"
            onClick={() => setDeleteTarget(value)}
            _hover={{ background: "pill.red.bg", color: "pill.red.text" }}
          >
            <LuTrash size="16" />
          </IconButton>
        </HStack>
      ),
    },
  ];

  const createButton = (
    <Button variant="solid" onClick={() => setIsDialogOpen(true)}>
      <LuPlus /> Create a conversion
    </Button>
  );

  const dialogs = (
    <>
      <CreateConversionActionDialog
        isOpen={isDialogOpen}
        onOpenChange={({ open }) => setIsDialogOpen(open)}
        onSuccess={loadConversionActions}
        platform={platform}
        workspaceSlug={workspaceSlug ?? ""}
      />
      <ConversionActionSyncedEventsDialog
        action={syncedEventsAction}
        isOpen={syncedEventsAction !== null}
        onOpenChange={({ open }) => {
          if (!open) setSyncedEventsAction(null);
        }}
        workspaceSlug={workspaceSlug ?? ""}
      />
      {workspaceSlug && platform === ConversionActionPlatform.META && (
        <ConversionActionTemplatesDialog
          isOpen={isTemplatesDialogOpen}
          onOpenChange={({ open }) => setIsTemplatesDialogOpen(open)}
          onSuccess={loadConversionActions}
          workspaceSlug={workspaceSlug}
        />
      )}
      <DialogRoot
        open={deleteTarget !== null}
        onOpenChange={({ open }) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle>Delete conversion?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Text color="fg.muted" fontFamily="inter" fontSize="sm">
              &ldquo;{deleteTarget?.name}&rdquo; will stop receiving conversions
              from {AGENT_NAME}. The conversion action in {adsLabel} is not
              deleted.
            </Text>
          </DialogBody>
          <DialogFooter>
            <Button variant="surface" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              loading={isDeleting}
              onClick={handleDelete}
              variant="destructive"
            >
              Delete conversion
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </>
  );

  const header = (
    <PageHeader
      title="Conversion Agent"
      subtitle={`Processes every conversion on your site and assigns its true value before sending it back to ${adsLabel}`}
    >
      {createButton}
    </PageHeader>
  );

  if (conversionActions.length === 0 && hasLoadedOnce) {
    return (
      <Page>
        <Stack flex="1" gap="6">
          {header}
          <PageBody>
            <HStack flex="1" alignItems="center">
              <EmptyState
                icon={<IconCircle Icon={LuTarget} size="md" variant="flat" />}
                title="No conversions yet"
                description={`Create a conversion to start sending ${AGENT_NAME}-valued conversions to ${adsLabel}.`}
              >
                <Stack align="center" gap="3">
                  {createButton}
                  {platform === ConversionActionPlatform.META &&
                    templates.length > 0 && (
                      <Button
                        variant="surface"
                        onClick={() => setIsTemplatesDialogOpen(true)}
                      >
                        Set up {AGENT_NAME} templates
                      </Button>
                    )}
                </Stack>
              </EmptyState>
            </HStack>
          </PageBody>
        </Stack>
        {dialogs}
      </Page>
    );
  }

  return (
    <Page loading={isLoading && !hasLoadedOnce}>
      <Stack flex="1" gap="6">
        {header}
        <PageBody>
          <DataTable
            data={conversionActions}
            columns={columns}
            skeleton={isLoading && hasLoadedOnce ? 3 : undefined}
            template="minmax(260px,1fr) 220px 132px"
            minTableWidth="560px"
            onRowClick={(value) => setSyncedEventsAction(value)}
          />
        </PageBody>
      </Stack>
      {dialogs}
    </Page>
  );
}
